import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, Subscription, of } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
  QueryStringTypes,
  QueryStringTypesEnum,
} from 'src/app/configs/query-string-types';
import { EventTypes } from 'src/app/core/constants/event-types';
import { ChildConfig } from 'src/app/core/models/child-config.model';
import { ConsoleService } from 'src/app/core/services/console.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { RoutingStateService } from 'src/app/core/services/routing-state.service';
import { propertiesProxy } from 'src/app/core/utils/property-proxy-helper';
import { localizer } from 'src/app/i18n';

import {
  ComparisonObjectsWithLogicalOperators,
  CustomResult,
} from 'src/app/core/api/models';
import { EnumsService } from 'src/app/core/api/services';
import { Orders } from 'src/app/core/models/dynamic-table/order.model';
import { Pagination } from 'src/app/core/models/dynamic-table/pagination.model';
import { TableColumn } from 'src/app/core/models/dynamic-table/table-column.model';
import { FormPageConfig } from 'src/app/core/models/form-page-config.model';
import { PageConfig } from 'src/app/core/models/page-config.model';
import { ApiNotificationService } from 'src/app/core/services/api-notification.service';
import {
  DialogService,
  confirmDialogConfig,
} from 'src/app/core/services/dialog.service';
import { DynamicTableService } from 'src/app/core/services/dynamic-table.service';
import { ConfirmDialogComponent } from 'src/app/shared/global/confirm-dialog/confirm-dialog.component';
import { BasePageComponent } from './base-page.component';

@Component({
  selector: 'app-base-page',
  template: ``,
  standalone: false,
})
export abstract class BaseIndexPageComponent
  extends BasePageComponent
  implements OnDestroy, OnInit
{
  //#region Properties
  routingState = inject(RoutingStateService);
  dialogService = inject(DialogService);
  apiNotificationService = inject(ApiNotificationService);
  dynamicTableService = inject(DynamicTableService);
  enumService = inject(EnumsService);
  route = inject(ActivatedRoute);
  location = inject(Location);
  protected tableDataSubject$ = new Subject<void>();
  isBindTableData: boolean = false;
  componentProperties = propertiesProxy(this);
  //#region Base Models
  modelWithPagination$: Observable<any>;
  items$: Observable<Array<any>>;
  items: Array<any>;
  pageConfig: PageConfig = {};
  formPageConfig: FormPageConfig<any> = {};
  idPropertyName: string = 'id';
  lastSelectedItemId: any = null;
  parentQueryString: string = '';
  //#endregion
  tableColumns: TableColumn[] = [];
  comparisonObjects: ComparisonObjectsWithLogicalOperators[] | any = []; //search field table Model
  sortObject: Orders[] = []; //sort field table Model
  paginationModel = {} as Pagination; //pagination table Model
  showParent = false;

  protected currentService: any;
  paginationApiMethodName: string;
  auditApiMethodName: string;
  deleteApiMethodName: string;
  getApiMethodName: string;
  createGetApiMethodName: string;
  #pageService: object;
  //#endregion

  //#region Rxjs

  subs: Subscription;

  baseObserver = {
    next: (result: CustomResult) => {
      this.apiNotificationService.notifyMessage({ result });
      if (result.isSucceed) {
        this.getTableData();
      }
    },
    error: (error) => {
      ConsoleService.error('error', error);
    },
    complete: () => (this.pageConfig.loading = false),
  };

  setParentQueryString() {
    if (!window.location.search.includes('key-'))
      this.parentQueryString = window.location.search;
  }

  getParentQueryString() {
    this.location.go(window.location.pathname + this.parentQueryString);
  }

  clearFiltersAndSetDefaultSort() {
    if (window.location.search.includes('key-')) {
      this.paginationModel.pageNumber = 1;
      this.paginationModel.pageSize = 30;
      this.comparisonObjects = [];
      this.sortObject = [];
      this.setDefaultTableSort();
    }
  }

  baseDeleteObserver = {
    next: (result: CustomResult) => {
      this.apiNotificationService.notifyMessage({ result });
      if (result.isSucceed) {
        this.deleteItemLocally();
        this.paginationModel.totalCount -= 1;
        if (this.paginationModel.totalCount == 0) {
          this.pageConfig.isEmpty = true;
        }
        this.pageConfig = Object.assign({}, this.pageConfig);
        this.getItems();
        this.selectedIds = [];
      }
    },
    error: (error) => {
      ConsoleService.error('error', error);
    },
    complete: () => (this.pageConfig.loading = false),
  };

  baseGroupDeleteObserver = {
    next: (result: CustomResult) => {
      this.apiNotificationService.notifyMessage({ result });
      if (result.isSucceed) {
        this.selectedIds = [];
        if (this.lastSelectedItemId.length == this.paginationModel.pageSize) {
          this.getTableData();
        } else {
          this.deleteGroupItemsLocally();
          this.paginationModel.totalCount -= this.lastSelectedItemId.length;
          if (this.paginationModel.totalCount == 0) {
            this.pageConfig.isEmpty = true;
          }
          this.pageConfig = Object.assign({}, this.pageConfig);
          this.getItems();
        }
      }
    },
    error: (error) => {
      ConsoleService.error('error', error);
      this.selectedIds = [];
    },
    complete: () => (this.pageConfig.loading = false),
  };

  baseGetByIdObserver = {
    next: (response) => {
      this.formPageConfig.editData = response?.data;
      this.pageConfig.isVisibleForm = true;
    },
    error: (error) => {
      ConsoleService.error('error', error);
      this.formPageConfig.isEditMode = false;
    },
    complete: () => (this.pageConfig.loading = false),
  };

  baseGetByIdObserverFormModal = {
    next: (response) => {
      this.formPageConfig.editData = response?.data;
      this.openEditFormModal(response?.data);
    },
    error: (error) => {
      ConsoleService.error('error', error);
    },
    complete: () => (this.pageConfig.loading = false),
  };

  //#endregion

  rowIndexSelected: number;
  tableVocabs: string;
  localizer = localizer;
  selectedIds: any[] = [];
  // currentDateTime: string = new Date().toISOString();
  currentDateTime: string = new Date(
    new Date().getTime() - new Date().getTimezoneOffset() * 60000
  ).toISOString();

  //#endregion

  constructor() {
    super();
  }

  ngOnInit(
    justGetItem?,
    callGetTableData = true,
    delayForCallGetTableData?
  ): void {
    this.handleShowParent();
    super.ngOnInit();
    this.setPageService();
    this.getVocabs();
    this.configTableColumns();
    this.initTablePagination();
    this.setDefaultTableSort();
    this.configPage();
    //! برای صفحاتی که چند جدول در یک صفحه وجود دارد todo rashidi
    if (this.routingState.isNotExceptedPages()) {
      // RoutingStateService.addComponentToState();
      if (window.location.search != '') {
        this.getComparisonObjects(this.convertFilters(), true);
        this.getSortItems(this.convertSort(), true);
        let params = this.routingState.parseQueryString();
        let pageSize = 30;
        for (const [key, value] of Object.entries(params))
          if (key == 'PageSize' && !isNaN(+value)) pageSize = +value;

        this.changePageSize(pageSize, true);
      }
    }
    this.getEnumsValues();

    if (justGetItem) {
      const t = setTimeout(() => {
        this.getItems();
        clearTimeout(t);
      }, delayForCallGetTableData || 0);
    } else if (callGetTableData) {
      const t = setTimeout(() => {
        this.getTableData();
        clearTimeout(t);
      }, delayForCallGetTableData || 0);
    }

    this.configFormPage();
    this.setPageAfterEnterUrl();
  }

  handleShowParent() {
    if (window.location.search.includes('key-')) this.showParent = false;
    else this.showParent = true;
  }

  /** زمانی که در یوآرال مشخص شده باشد باید چایلد کامپوننتی باز شود استفاده می شود */
  onShowChildPage(config: ChildConfig) {
    this.showParent = false;
    this.setParentQueryString();
    if (config.setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          config.queryStringKey +
          '=' +
          config.values.join('_')
      );
    }
    config.class[config.showPagePropKey] = true;
    config.selectedPropKey.forEach((propKey, index) => {
      switch (propKey.keyType) {
        case 'number':
          config.class[propKey.keyName] = +config.values[index];
          break;
        case 'boolean':
          config.class[propKey.keyName] = !!config.values[index];
          break;
        default:
          config.class[propKey.keyName] = config.values[index];
      }
    });
  }

  convertFilters() {
    let params = this.routingState.parseQueryString();
    const result = [];
    const tempMap = new Map();

    for (const [key, value] of Object.entries(params)) {
      if (
        key.toLowerCase() != QueryStringTypes.PageNumber &&
        key.toLowerCase() != QueryStringTypes.PageSize &&
        !key.startsWith(QueryStringTypes.Sort) &&
        !key.startsWith('key-') &&
        value != 'undefined'
      ) {
        const [field, operator, logicalOperator] = key.split('_');
        const operatorInt = +operator;
        const logicalOperatorInt =
          logicalOperator !== undefined ? +logicalOperator : null;

        if (logicalOperatorInt !== null) {
          const entryKey = `${logicalOperatorInt}_${field}`;
          if (!tempMap.has(entryKey)) {
            tempMap.set(entryKey, {
              comparisonObjects: [],
              logicalComparisonOperator: logicalOperatorInt,
            });
          }
          tempMap.get(entryKey).comparisonObjects.push({
            field: field,
            operator: operatorInt,
            value: value
              .toString()
              // .replaceAll('+', ' ')
              .replaceAll('%20', ' ')
              // برای فیکس کردن فرمت ساعت ها
              .replace(
                /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}):(\d{2})/g,
                '$1.$2'
              )
              .replace(
                /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.)(\d{3})([+-]\d{2}:\d{2})/g,
                '$1$2'.substring(0, 21) + '$3'
              ),
            // .replaceAll('%2B', '+'),
          });
        } else {
          result.push({
            comparisonObjects: [
              {
                field: field,
                operator: operatorInt,
                value: value
                  .toString()
                  //  .replaceAll('+', ' ')
                  .replaceAll('%20', ' ')
                  .replace(
                    /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}):(\d{2})/g,
                    '$1.$2'
                  )
                  .replace(
                    /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.)(\d{3})([+-]\d{2}:\d{2})/g,
                    '$1$2'.substring(0, 21) + '$3'
                  ),
                //  .replaceAll('%2B', '+'),
              },
            ],
          });
        }
      }
    }
    for (const entry of tempMap.values()) {
      result.push(entry);
    }
    return result;
  }

  convertSort() {
    let params = this.routingState.parseQueryString();
    let result = [];
    for (const [key, value] of Object.entries(params)) {
      if (key.startsWith('sort')) {
        result.push({
          columnName: value,
          sort: +key.split('_')[1],
        });
      }
    }
    return result;
  }

  //#region initialize

  setPageService(pageService?: object, createGetApiMethodName?: string) {
    this.#pageService = pageService || this.currentService;
    this.createGetApiMethodName =
      createGetApiMethodName || this.createGetApiMethodName;
  }

  getEnumsValues() {}

  getVocabs() {
    this.pageVocabs = `pages.${this.pageName}.`;
    this.tableVocabs = this.pageVocabs + 'form.fields.';
  }

  initTablePagination() {
    this.paginationModel.pageIndex = 1;
    this.paginationModel.firstIndex = 1;

    this.paginationModel.pageNumber = 1;
    this.paginationModel.pageSize = 30;
  }

  setDefaultTableSort() {
    try {
      //اگر از قبل مقدار وجود داشت دیگه push نکنه
      const isExistsSortObject = this.sortObject.some((item) =>
        item.columnName.includes('created')
      );
      if (!isExistsSortObject)
        this.sortObject.push({ columnName: 'created', sort: 1 });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  configPage() {
    this.pageConfig = {
      title: this.pageVocabs + 'title',
      isShowTable: true,
      showHeader: true,
      showDefaultBtns: true,
      showAuditBtn: true,
      showAddBtn: true,
      showRefreshBtn: false,
      showBackBtn: true,
      showTopHeaderBorderBottom: true,
      showLoadingBar: true,
      showHelpBtn: true,
      isAddBtnWithLoading: true,
      tableConfig: {
        isActiveTableStriped: true,
        pageTagName: this.tagName,
        hidePageSize: false,
        showTableShadow: true,
        preloadingRowCount: 10,
        tableColumns: this.tableColumns,
        paginationModel: this.paginationModel,
        comparisonObjects: this.comparisonObjects,
        sortObject: this.sortObject,
        hasTableResponsive: true,
        showOperationColumn: true,
        showDeleteFilters: true,
      },
    };
  }

  configFormPage() {
    this.formPageConfig.isEditMode = false;
    this.formPageConfig.vocabs = this.pageVocabs + 'form.';
  }

  getTableData() {
    this.bindTableData();
    this.tableDataSubject$.next();
    this.pageConfig.loading = true;
    this.pageConfig.hasError = false;
    this.unCheckSelectAll();
    this.removePopovers();
  }

  removePopovers() {
    const popovers = document.querySelectorAll('message-popover');
    popovers.forEach((p) => {
      p?.remove();
    });
  }

  bindTableData() {
    if (this.isBindTableData) return;
    this.isBindTableData = true;
    this.tableDataSubject$
      .pipe(
        switchMap((res) => {
          this.pageConfig.loading = true;
          return this.getPaginationApi().pipe(
            map((res) => res.data),
            tap((result) => {
              this.modelWithPagination$ = of(result);
            }),
            finalize(() => (this.pageConfig.loading = false)),
            catchError((error) => {
              this.pageConfig.isEmpty = false;
              this.pageConfig.hasError = true;
              return error;
            }),
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.pageConfig.loading = false;
            })
          );
        })
      )
      .subscribe({
        next: (res) => {
          this.checkSelectedItems();
          this.getPaginationData(res);
          this.items = res.items;
          this.items$ = of(this.items);
          res.items?.length > 0
            ? (this.pageConfig.isEmpty = false)
            : (this.pageConfig.isEmpty = true);
          this.pageConfig.loading = false;
        },
        error: (error) => {
          ConsoleService.error('error', error);
          this.isBindTableData = false;
        },
      });
  }

  getItems() {
    try {
      if (
        !this.lastSelectedItemId ||
        this.lastSelectedItemId.length == this.paginationModel.pageSize
      ) {
        this.pageConfig.loading = true;
        this.items = [];

        //مدلی که در ویو ساب اسکرایب میشه
        this.items$ = this.modelWithPagination$.pipe(
          map((res) => res.data),
          tap((item) => {
            this.getPaginationData(item);
          }),
          map((item) => item?.items),
          tap((items) => {
            this.items = items;
            items?.length > 0
              ? (this.pageConfig.isEmpty = false)
              : (this.pageConfig.isEmpty = true);
          }),
          takeUntil(this.onDestroy$),
          finalize(() => {
            this.pageConfig.loading = false;
          })
        );
      } else {
        this.items$ = of(this.items);
        this.lastSelectedItemId = null;
        this.pageConfig.loading = false;
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  abstract configTableColumns(): void;

  //#endregion

  //#region Api & Http Request

  //Get All
  getPaginationApi(): Observable<any> {
    if (!this.currentService || !this.paginationApiMethodName) return of();
    return this.currentService[this.paginationApiMethodName]({
      body: this.getBaseModelWithPaginationParams(),
    });
  }

  //Delete
  baseDeleteApi(id: any, idPropName: string = 'id') {
    if (!this.currentService || !this.deleteApiMethodName) return;
    this.unSubs();
    this.pageConfig.loading = true;
    this.lastSelectedItemId = id;
    this.currentService[this.deleteApiMethodName]({ [idPropName]: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseDeleteObserver);
  }

  //Get ById
  baseGetByIdApi(id: any) {}

  //Get ById
  setPageAfterEnterUrl() {
    // وارد میشود چک میکنیم که کدام جدول باید نمایش داده شود url زمانی که کاربر با
    let params = this.routingState.parseQueryString();
    for (const [key, value] of Object.entries(params)) {
      if (key == QueryStringTypesEnum.Update_key) this.onUpdate(value, false);
      if (key == 'key-formType' && value == 'create') this.onCreate('', false);
    }
  }

  //Params
  getBaseModelWithPaginationParams(): object {
    return {
      pageNumber:
        this.paginationModel.pageSize > this.paginationModel.totalCount
          ? 1
          : this.paginationModel.pageNumber,
      pageSize: this.paginationModel.pageSize,
      comparisonObjects: this.comparisonObjects,
      orders: this.sortObject,
    };
  }

  //#endregion

  //#region Form

  getFormResponse(isSuccess) {
    this.showParent = true;
    this.getParentQueryString();
    if (isSuccess) {
      //  this.onClearQueryString();
      this.getTableData();
      this.selectedIds = [];
    }

    this.pageConfig.isVisibleForm = false;

    // this.getParentQueryString();

    // this.formPageConfig = {};
    // this.unSubs();
  }

  //#endregion

  //#region Crud Events

  onDelete(id: any, content: any, confirmByWritingText?: boolean) {
    const dialogResult = this.getConfirmDialog({
      content: content,
      isConfirmDelete: true,
      confirmByWritingText: confirmByWritingText,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.pageConfig.loading = true;
          this.baseDeleteApi(id);
        }
      });
  }

  onCreate(type?: any, isFormModal?: boolean) {
    try {
      this.#pageService
        [this.createGetApiMethodName]()
        .pipe(this.basePipeOperators())
        .subscribe(
          (res) => {
            if (res) {
              if (isFormModal) {
                const timeout = setTimeout(() => {
                  this.pageConfig.isVisibleForm = false;
                  clearTimeout(timeout);
                }, 10);
                this.openCreateFormModal();
              } else {
                this.setParentQueryString();

                this.location.go(
                  window.location.pathname + '?key-formType=create'
                );
                this.pageConfig.isVisibleForm = true;
              }
            }
          },
          (error) => {
            this.pageConfig.isVisibleForm = undefined;
            const timeout = setTimeout(() => {
              this.pageConfig.isVisibleForm = false;
              clearTimeout(timeout);
            }, 10);
          }
        );
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  openCreateFormModal() {}

  openEditFormModal(data: any) {}

  onUpdate(id?, setUrl = true) {
    this.showParent = false;
    this.setParentQueryString();
    if (setUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Update_key +
          '=' +
          id
      );
    }
    this.formPageConfig.editData = {};
    this.formPageConfig.isEditMode = true;
    this.baseGetByIdApi(id);
  }

  getConfirmDialog(
    config: confirmDialogConfig
  ): MatDialogRef<ConfirmDialogComponent, any> {
    try {
      return this.dialogService.confirmDialog(config);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  //#endregion

  //#region Audit Page
  onShowAuditPage() {
    // this.setFiltersInState();
    this.pageConfig.isVisibleAuditPage = true;
  }

  onHideAuditPage() {
    this.pageConfig.loading = true;
    let timeOut = setTimeout(() => {
      this.pageConfig.loading = false;
      clearTimeout(timeOut);
    }, 50);
    this.showParent = true;
    this.getParentQueryString();
    this.pageConfig.isVisibleAuditPage = false;
  }
  //#endregion

  //#region help Page

  onShowHelpPage() {
    this.pageConfig.isVisibleHelpPage = true;
  }

  onHideHelpPage() {
    this.pageConfig.loading = true;
    let timeOut = setTimeout(() => {
      this.pageConfig.loading = false;
      clearTimeout(timeOut);
    }, 50);
    this.showParent = true;
    this.getParentQueryString();
    this.pageConfig.isVisibleHelpPage = false;
  }

  //#endregion

  //#region Table Pagination

  changePageNumber(pageNumber: number, isOnLoadData: boolean = false) {
    this.paginationModel.pageNumber = pageNumber;
    if (!isOnLoadData) this.getTableData();
  }

  changePageSize(pageSize: number, isOnLoadData: boolean = false) {
    if (+pageSize > 200) pageSize = 200;

    this.paginationModel.pageSize = +pageSize;

    if (!isOnLoadData) {
      const reg = /PageNumber=[0-9]+/;

      this.location.go(
        window.location.pathname +
          window.location.search.replace(reg, 'PageNumber=1')
      );

      this.paginationModel.pageNumber = 1;

      this.getTableData();
    }
  }

  // getPaginationData(data) {
  //   this.paginationModel = this.dynamicTableService.getPaginationData(data, this.paginationModel);
  //   this.pageConfig.tableConfig.paginationModel = this.paginationModel;
  // }

  getPaginationData(model) {
    try {
      if (!model) return;

      let totalPages = Math.ceil(
        model?.totalCount / this.paginationModel?.pageSize
      );
      let data = {
        totalCount: model.totalCount,
        totalPages: totalPages,
        pageIndex: this.paginationModel.pageIndex,
        pageNumber: this.paginationModel.pageNumber,
        hasPreviousPage: this.paginationModel.pageNumber > 1 ? true : false,
        hasNextPage:
          this.paginationModel.pageNumber < totalPages ? true : false,
      };
      this.paginationModel = this.dynamicTableService.getPaginationData(
        data,
        this.paginationModel
      );
      this.pageConfig.tableConfig.paginationModel = this.paginationModel;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  //#endregion

  //#region Table Search

  getComparisonObjects(
    model: ComparisonObjectsWithLogicalOperators[] | any,
    isOnLoadData: boolean = false
  ) {
    this.comparisonObjects = model;

    let pageNumber = 1;

    if (this.routingState.isNotExceptedPages()) {
      let params = this.routingState.parseQueryString();
      for (const [key, value] of Object.entries(params)) {
        if (key == 'PageNumber') {
          pageNumber = +value;
        }
      }
    }
    const reg = /PageNumber=[0-9]+/;

    this.location.go(
      window.location.pathname +
        window.location.search.replace(reg, 'PageNumber=' + pageNumber)
    );

    this.paginationModel.pageNumber = 1;
    this.pageConfig.tableConfig.comparisonObjects = this.comparisonObjects;
    if (this.loading) return;

    //decode searched value
    if (this.comparisonObjects)
      for (let i = 0; i < this.comparisonObjects.length; i++)
        this.comparisonObjects[i].comparisonObjects.forEach((element) => {
          element.value = decodeURIComponent(element.value);
        });

    if (!isOnLoadData) {
      this.getTableData();
    }
  }

  //#endregion

  //#region Table Sort

  getSortItems(model: Orders[], isOnLoadData: boolean = false) {
    this.sortObject = [];
    if (model?.length === 0) this.setDefaultTableSort();
    else this.sortObject = model;

    this.pageConfig.tableConfig.sortObject = this.sortObject;

    this.unSubs();

    if (!isOnLoadData) {
      this.getTableData();
    }
  }

  //#endregion

  //#region Group selection

  //this method checks the 'select-all' checkbox if all items are checked
  isSelectAll(checkBoxAllElement) {
    try {
      let checkboxes = this.elementRef.nativeElement.querySelectorAll(
        `input[type="checkbox"]:checked`
      );
      //to check if 'select all' checkBox exists in checkboxes list
      let isExistsSelectAll = checkboxes[0].classList.contains('select-all');
      if (!isExistsSelectAll) {
        if (
          (this.paginationModel.totalCount < this.paginationModel.pageSize &&
            checkboxes.length == this.paginationModel.totalCount) ||
          checkboxes.length == this.paginationModel.pageSize
        ) {
          (checkBoxAllElement[0] as HTMLInputElement).checked = true;
        }
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  onAddSelectedId(event, selectedId: any, element) {
    try {
      const checkBoxAllElement =
        this.elementRef.nativeElement.querySelectorAll(`.select-all`);

      let checked = event?.target?.checked;
      if (!selectedId) return;

      if (checked) {
        //add
        this.isSelectAll(checkBoxAllElement);
        if (this.selectedIds?.find((id) => id === selectedId)) return;
        this.selectedIds?.push(selectedId);
        (element as HTMLElement).classList.add('row-active');
      } else {
        //remove
        const id = this.selectedIds?.findIndex((id) => id === selectedId);
        if (id != -1) this.selectedIds?.splice(id, 1);
        (element as HTMLElement).classList.remove('row-active');
        if ((checkBoxAllElement[0] as HTMLInputElement).checked)
          this.unCheckSelectAll();
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  unCheckSelectAll() {
    try {
      const checkBoxAllElement = this.elementRef.nativeElement.querySelectorAll(
        `.select-all`
      )[0] as HTMLInputElement;
      if (checkBoxAllElement) checkBoxAllElement.checked = false;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  // checkbox
  isChecked(selectedId: any, element): boolean {
    try {
      if (this.selectedIds.find((id) => id === selectedId)) {
        (element as HTMLElement).classList.add('row-active');
        return true;
      }

      return false;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  onSelectAll(event) {
    try {
      const checkBoxElements =
        this.elementRef.nativeElement.querySelectorAll(`.form-check-input`);
      checkBoxElements.forEach((checkBoxElement) => {
        if (!checkBoxElement.classList.contains('select-all')) {
          (checkBoxElement as HTMLInputElement).checked = !event;
          (checkBoxElement as HTMLInputElement).click();
        }
      });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  async checkSelectedItems() {
    if (!this.selectedIds?.length) return;

    let checkBoxes = await this.getAllCheckBoxesAsync();

    checkBoxes.forEach((element) => {
      const itemId = element.getAttribute('data-checkbox-id');
      if (this.selectedIds.includes(+itemId))
        (element as HTMLInputElement).checked = true;
    });
  }

  async getAllCheckBoxesAsync(): Promise<NodeListOf<Element>> {
    try {
      return AppDomService.getElementsByQueryAsync(
        `${this.tagName} [data-checkbox-id]`
      );
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  //#endregion

  //#region Group Deletion

  callGroupDeletion() {}

  groupDeletionConfirmation(ids: number[]) {
    const dialogResult = this.getConfirmDialog({
      title: 'messages.groupDeletionConfirmation',
      content: ids.join().replace(/,/g, ' - '),
      isConfirmDelete: true,
      wordBreak: false,
      isShowChip: true,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.callGroupDeletion();
          this.selectedIds = [];
          this.unCheckSelectAll();
        }
      });
  }

  onGroupDeletion() {
    if (this.selectedIds?.length === 0) return;
    this.groupDeletionConfirmation(this.selectedIds);
  }

  //#endregion

  //#region Group Resend

  callGroupResendApi(ids?: number[]) {}

  groupResendConfirmation(ids: number[]) {
    const dialogResult = this.getConfirmDialog({
      title: 'messages.resendMessageConfirmation',
      content: ids.join().replace(/,/g, ' - '),
      isConfirmDelete: false,
      wordBreak: false,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) this.callGroupResendApi(ids);
      });
  }

  onGroupResend() {
    if (this.selectedIds?.length === 0) return;
    this.groupResendConfirmation(this.selectedIds);
  }

  //#endregion

  onActiveRow(rowElement, index?: number) {
    try {
      this.rowIndexSelected = index;
      const oldRowActive = document.querySelector(
        `${this.tagName} .row-active`
      ) as HTMLElement;

      if (oldRowActive) {
        oldRowActive.classList.remove('row-active');
      }
      (rowElement as HTMLElement).classList.add('row-active');
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  //#region local delete
  deleteItemLocally() {
    try {
      const itemIndex = this.items.findIndex(
        (item) => item[this.idPropertyName] == this.lastSelectedItemId
      );
      if (itemIndex > -1) this.items.splice(itemIndex, 1);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  deleteGroupItemsLocally() {
    try {
      this.items = this.items.filter(
        (item) => !this.lastSelectedItemId.includes(item[this.idPropertyName])
      );
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.baseIndexPage
      );
    }
  }

  onClearQueryString() {
    this.getComparisonObjects(this.convertFilters(), true);
    this.getSortItems(this.convertSort(), true);
    this.changePageSize(30, true);
    this.getTableData();
  }
  //#endregion

  ngOnDestroy() {
    super.ngOnDestroy();
    this.tableDataSubject$.complete();
    this.tableDataSubject$.unsubscribe();
  }

  onReset() {
    this.getTableData();
  }
  
}
